import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Loader2,
  TrendingUp,
  Share2,
} from "lucide-react";
import { toast } from "sonner";

import ScoreRing from "@/components/ScoreRing";
import { api } from "@/lib/api";

const SCORE_META = [
  { key: "psychology_score", label: "Psychology", color: "hsl(222 100% 55%)" },
  { key: "discipline_score", label: "Discipline", color: "hsl(163 74% 42%)" },
  { key: "risk_score", label: "Risk", color: "hsl(35 92% 55%)" },
  { key: "strategy_score", label: "Strategy", color: "hsl(280 65% 60%)" },
];

function verdict(score) {
  if (score >= 80) return { label: "Elite", color: "text-emerald-500" };
  if (score >= 65) return { label: "Solid", color: "text-primary" };
  if (score >= 45) return { label: "Developing", color: "text-amber-500" };
  return { label: "At Risk", color: "text-red-500" };
}

export default function Result() {
  const { id } = useParams();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/assessment/${id}`);
        setAssessment(res.data);
      } catch {
        toast.error("Report not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading your report…</p>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-muted-foreground">Report not found.</p>
      </div>
    );
  }

  const v = verdict(assessment.overall_score);

  const shareReport = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({
          title: "My MethodA Trading Health Report",
          text: `I scored ${assessment.overall_score}/100 on the MethodA Trading Health Assessment.`,
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Report link copied to clipboard");
      }
    } catch {
      // user cancelled — silent
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 sm:py-14">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4"
      >
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            Your report
          </p>
          <h1 className="font-display mt-1 text-3xl sm:text-4xl font-semibold tracking-tight">
            Hey {assessment.name.split(" ")[0]}, here&rsquo;s your trading health.
          </h1>
          <p className="mt-2 text-muted-foreground">
            Verdict:{" "}
            <span className={`font-semibold ${v.color}`}>{v.label}</span> · Overall{" "}
            {assessment.overall_score}/100
          </p>
        </div>
        <button
          onClick={shareReport}
          data-testid="share-report-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-accent transition self-start sm:self-auto"
        >
          <Share2 className="w-4 h-4" />
          Share
        </button>
      </motion.div>

      {/* Overall + Sub-scores */}
      <div className="mt-10 grid lg:grid-cols-[1fr_1.5fr] gap-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="p-8 rounded-2xl border border-border bg-card soft-shadow flex flex-col items-center justify-center"
          data-testid="overall-score-card"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Overall Score
          </p>
          <div className="mt-4">
            <ScoreRing
              value={assessment.overall_score}
              label={v.label}
              size={200}
              stroke={14}
              testId="overall"
            />
          </div>
        </motion.div>

        <div className="grid grid-cols-2 gap-4">
          {SCORE_META.map((s, i) => (
            <motion.div
              key={s.key}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 + i * 0.05 }}
              className="p-5 rounded-2xl border border-border bg-card soft-shadow flex flex-col items-center"
            >
              <ScoreRing
                value={assessment[s.key]}
                label={s.label}
                size={120}
                stroke={10}
                color={s.color}
                testId={s.label.toLowerCase()}
              />
            </motion.div>
          ))}
        </div>
      </div>

      {/* Strengths & Weaknesses */}
      <div className="mt-10 grid md:grid-cols-2 gap-4">
        <ListCard
          title="What's working"
          icon={CheckCircle2}
          items={assessment.strengths}
          tone="success"
          testId="strengths"
        />
        <ListCard
          title="What's breaking you"
          icon={AlertTriangle}
          items={assessment.weaknesses}
          tone="warning"
          testId="weaknesses"
        />
      </div>

      {/* Recommendations */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15 }}
        className="mt-6 p-6 sm:p-8 rounded-2xl border border-border bg-card soft-shadow"
        data-testid="recommendations-card"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              Personalized playbook
            </p>
            <h2 className="font-display text-2xl font-semibold tracking-tight">
              Do these next
            </h2>
          </div>
        </div>
        <ol className="mt-6 space-y-3">
          {assessment.recommendations.map((r, i) => (
            <li
              key={i}
              className="flex gap-3 p-4 rounded-xl border border-border/60 hover:border-primary/40 transition"
              data-testid={`recommendation-${i}`}
            >
              <span className="text-sm font-semibold text-primary tabular-nums shrink-0">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-sm text-foreground/90 leading-relaxed">{r}</span>
            </li>
          ))}
        </ol>
      </motion.div>

      {/* CTA */}
      <div className="mt-10 rounded-2xl border border-border bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-8 sm:p-12">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary">
              <TrendingUp className="w-4 h-4" />
              NEXT STEP
            </div>
            <h3 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
              Improve your trading with MethodA
            </h3>
            <p className="mt-2 text-muted-foreground max-w-xl">
              Weekly reviews, checklists and 1:1 mentorship built around your weakest score.
            </p>
          </div>
          <Link
            to="/thank-you"
            data-testid="result-cta-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-medium hover:-translate-y-[1px] transition self-start sm:self-auto"
          >
            I&rsquo;m in — what&rsquo;s next?
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function ListCard({ title, icon: Icon, items, tone, testId }) {
  const toneMap = {
    success:
      "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
    warning: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
  };
  return (
    <div
      className="p-6 rounded-2xl border border-border bg-card soft-shadow"
      data-testid={`list-${testId}`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center border ${toneMap[tone]}`}
        >
          <Icon className="w-4 h-4" />
        </div>
        <h3 className="font-display text-lg font-semibold tracking-tight">{title}</h3>
      </div>
      <ul className="mt-5 space-y-3">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-foreground/90">
            <span className="text-muted-foreground mt-0.5">·</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
